"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Semaphore = void 0;
let semaphoreNumber = 0;
class Semaphore {
    /**
     * Creates a new semaphore container.
     * @param count Number of available semaphores (default: 1)
     * @param opts Additional options for the semaphore
     */
    constructor(count = 1, opts) {
        var _a;
        this._nextSemaphoreId = 0;
        this._maxSemaphores = count;
        this.name = (_a = opts === null || opts === void 0 ? void 0 : opts.name) !== null && _a !== void 0 ? _a : `anonymous ${semaphoreNumber++}`;
        this._loggedName = `“${this.name}”`;
        this._logger = opts === null || opts === void 0 ? void 0 : opts.logger;
        this._currentSemaphores = new Set();
        this._waitingCallers = [];
    }
    /**
     * Try to immediately acquire a new semaphore.
     * If a semaphore is free, it is returned. If no semaphore is free, an error is thrown.
     * @param reason Reason for acquiring this semaphore, for logging purposes
     */
    tryAcquire(reason) {
        var _a, _b;
        const semaphoreId = this.nextSemaphoreId();
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.trace(`Trying acquire for semaphore ${this._loggedName} #${semaphoreId}${reason ? ` (reason: ${reason})` : ''} …`);
        if (this.hasAvailableSemaphores) {
            (_b = this._logger) === null || _b === void 0 ? void 0 : _b.trace(`tryAcquire() successful for semaphore ${this._loggedName} #${semaphoreId}`);
            this._currentSemaphores.add(semaphoreId);
            return this.createReleaseFunction(semaphoreId);
        }
        else {
            throw new Error('No semaphore available.');
        }
    }
    /**
     * Acquire a new semaphore.
     * The returned promise resolves as soon as a semaphore is available and returns a function `release`
     * which has to be used to release the acquired promise.
     * @param reason Reason for acquiring this semaphore, for logging purposes
     * @return {Promise<Function>}
     */
    acquire(reason) {
        var _a;
        const semaphoreId = this.nextSemaphoreId();
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.trace(`Starting acquire for semaphore ${this._loggedName} #${semaphoreId}${reason ? ` (reason: ${reason})` : ''} …`);
        const addCallerToWaitlist = (resolve) => {
            const release = this.createReleaseFunction(semaphoreId);
            this._waitingCallers.push(() => {
                var _a;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.trace(`acquire() successful for semaphore ${this._loggedName} #${semaphoreId}`);
                resolve(release);
                return semaphoreId;
            });
        };
        const treatPendingCallersNow = () => this.treatPendingCallers();
        const promise = new Promise(addCallerToWaitlist);
        setTimeout(treatPendingCallersNow, 0);
        return promise;
    }
    /**
     * Release the given semaphore.
     * This is usually done from inside the resolve callback because the ID is only known there.
     */
    release(id) {
        var _a;
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.trace(`Releasing semaphore ${this._loggedName} #${id}`);
        if (this._currentSemaphores.has(id)) {
            this._currentSemaphores.delete(id);
        }
        const treatPendingCallersAfterRelease = () => this.treatPendingCallers();
        setTimeout(treatPendingCallersAfterRelease, 0);
    }
    nextSemaphoreId() {
        return this._nextSemaphoreId++;
    }
    get hasAvailableSemaphores() {
        return this._currentSemaphores.size < this._maxSemaphores;
    }
    /**
     * @param id ID of the semaphore which is released when calling this function
     */
    createReleaseFunction(id) {
        const releaseMySemaphore = () => this.release(id);
        return releaseMySemaphore;
    }
    /**
     * When a semaphore is requested, the request is added to a queue.
     * The queue is processed by this function.
     */
    treatPendingCallers() {
        var _a, _b;
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.trace(`Checking for free semaphore ${this._loggedName}: ${this._waitingCallers.length} waiting`);
        while (this._waitingCallers.length > 0 && this.hasAvailableSemaphores) {
            const caller = this._waitingCallers.shift();
            if (caller !== undefined) {
                (_b = this._logger) === null || _b === void 0 ? void 0 : _b.trace(`Free semaphore ${this._loggedName} found, assigning.`);
                const semaphoreId = caller();
                this._currentSemaphores.add(semaphoreId);
            }
        }
    }
}
exports.Semaphore = Semaphore;
exports.default = Semaphore;
//# sourceMappingURL=index.js.map